import React, { useState } from 'react';
import { SalvBanner, SalvBannerType, SalvButton, SalvModal, SalvTagsInput } from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunScenariosForTransactionsByIds } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioHandles: string[];
  transactionIds: string[];
}

const validationSchema = Yup.object().shape({
  scenarioHandles: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one scenario handle is required')
    .required(),
  transactionIds: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one transaction ID is required')
    .test(
      'all-numeric',
      'All transaction IDs must be valid numbers',
      (values) => !values || values.every((v) => !!v && !isNaN(Number(v)))
    )
    .required(),
});

const RerunByTxIdsModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunScenariosForTransactionsByIds();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioHandles: [], transactionIds: [] };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioHandles: values.scenarioHandles,
        transactionIds: values.transactionIds.map(Number),
      });
      dispatch(showFeedback({ message: 'Scenario rerun by transaction IDs triggered successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, errors, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Scenarios for Transactions by IDs</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTagsInput
                value={values.scenarioHandles}
                onChange={(newValues) => setFieldValue('scenarioHandles', newValues)}
                label='Scenario Handles'
                placeholder='Enter scenario handle'
                isInvalid={!!errors.scenarioHandles}
                className='m-b-16'
              />
              <SalvTagsInput
                value={values.transactionIds}
                onChange={(newValues) => setFieldValue('transactionIds', newValues)}
                label='Transaction IDs'
                placeholder='Enter transaction ID'
                isInvalid={!!errors.transactionIds}
              />
              {errors.transactionIds && typeof errors.transactionIds === 'string' && (
                <div className='salv-error-message'>{errors.transactionIds}</div>
              )}
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunByTxIdsModal;
